
import { apiBase, authToken } from './apiBase';
// voiceEngine.ts — client for the SEPARATE voice-engine axis (GET/POST /api/voice/engine).
//
// This surfaces the manual override for the voice ENGINE (local Qwen PRIMARY vs Deepgram
// cloud) — it is NOT the F1/D4 reasoning routing_mode (which governs the reasoning model;
// voice never populates it). Local-primary is default-on in the backend and auto-falls-back
// to Deepgram ONLY when the DGX/service is unreachable; this control adds the deliberate
// "force Deepgram" override (and back to local) — nothing else.
//
// Dependency-free by design (no socket.io import) so the settings surface and its unit test
// stay light. The prod/dev base mirrors api/voice.ts::restApiBase intentionally.

export type VoiceEngineMode = 'local' | 'deepgram';

export interface VoiceEngineStatus {
  mode: VoiceEngineMode;
  localService: string | null;
  localHealthy: boolean;
}

// apiBase and authToken are SHARED (src/api/apiBase.ts). This file used to
// carry its own copy of both, as five sibling clients did — six identical
// hardcodings of a host, and a built bundle pointed every one of them at
// herexy.com wherever it was served from. An instance fix leaves twins, so the
// answer moved to one module rather than being corrected here.


// Backend returns { success, mode, local_service, local_healthy }. Fail safe: an unknown/
// missing mode reflects as 'local' (the default-on primary), never a spurious 'deepgram'.
export function normalizeEngineStatus(d: any): VoiceEngineStatus {
  return {
    mode: d && d.mode === 'deepgram' ? 'deepgram' : 'local',
    localService: d && d.local_service ? String(d.local_service) : null,
    localHealthy: !!(d && d.local_healthy),
  };
}

async function engineFetch(init: RequestInit): Promise<VoiceEngineStatus> {
  const res = await fetch(`${apiBase()}/voice/engine`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken()}`,
      ...(init.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`voice_engine_http_${res.status}`);
  return normalizeEngineStatus(await res.json());
}

/** GET the live voice-engine state (current mode + local-service reachability). */
export function getVoiceEngine(): Promise<VoiceEngineStatus> {
  return engineFetch({ method: 'GET' });
}

/** POST the voice-engine axis (manual override). Returns the effective state. */
export function setVoiceEngine(mode: VoiceEngineMode): Promise<VoiceEngineStatus> {
  return engineFetch({ method: 'POST', body: JSON.stringify({ mode }) });
}
